// Master Admin → Support / Complaints management (master-admin only).
// Dashboard stats, clickable notifications, filtered+paginated ticket list,
// per-ticket management (status / priority / assign / reply) and Excel export.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Pagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  IconButton,
  Menu,
  Divider,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';

const API = (p = '') => `${config.API_BASE_URL}/support${p}`;
const CO_API = (p = '') => `${config.API_BASE_URL}/superadmin/company${p}`;
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});
const PAGE_SIZE = 10;

const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const PRIORITIES = ['Low', 'Medium', 'High'];
const TYPES = ['Billing', 'Technical', 'Feature Request', 'Other'];

const statusColor = (s) =>
  s === 'Open' ? 'warning' : s === 'In Progress' ? 'info' : s === 'Resolved' ? 'success' : 'default';
const prioColor = (p) => (p === 'High' ? 'error' : p === 'Medium' ? 'warning' : 'default');

const Stat = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E5E7EB', borderLeft: `4px solid ${color}` }}>
    <Typography variant='caption' sx={{ color: '#6B7280', fontWeight: 600 }}>{label}</Typography>
    <Typography variant='h4' sx={{ fontWeight: 800, mt: 0.5 }}>{value ?? 0}</Typography>
  </Paper>
);

export default function SupportAdmin() {
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', status: 'all', priority: 'all',
    companyId: '', type: 'all', customer: '', ticketId: '', sort: 'newest',
  });

  const [notif, setNotif] = useState({ count: 0, items: [] });
  const [notifEl, setNotifEl] = useState(null);

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');

  const qs = useCallback(
    (extra = {}) => {
      const f = { ...filters, page, pageSize: PAGE_SIZE, ...extra };
      const p = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => {
        if (v !== '' && v != null && !(typeof v === 'string' && v === 'all')) p.append(k, v);
      });
      return p.toString();
    },
    [filters, page],
  );

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(API('/admin/stats'), { headers: headers() });
      if (r.ok) setStats(await r.json());
    } catch {}
  }, []);

  const loadNotif = useCallback(async () => {
    try {
      const r = await fetch(API('/admin/notifications'), { headers: headers() });
      if (r.ok) setNotif(await r.json());
    } catch {}
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const r = await fetch(CO_API('?pageSize=1000'), { headers: headers() });
      if (r.ok) {
        const d = await r.json();
        setCompanies(Array.isArray(d.items) ? d.items : []);
      }
    } catch {}
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API(`/admin/tickets?${qs()}`), { headers: headers() });
      if (r.ok) {
        const d = await r.json();
        setRows(d.items || []);
        setTotal(d.total || 0);
      } else if (r.status === 403) {
        toast.error('Master admin access only.');
      }
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => { loadStats(); loadNotif(); loadCompanies(); }, [loadStats, loadNotif, loadCompanies]);
  useEffect(() => { loadList(); }, [loadList]);

  const setF = (k) => (e) => { setPage(1); setFilters((s) => ({ ...s, [k]: e.target.value })); };

  const openTicket = async (id) => {
    setOpenId(id);
    setDetail(null);
    try {
      const r = await fetch(API(`/ticket/${id}`), { headers: headers() });
      if (r.ok) setDetail(await r.json());
    } catch {
      toast.error('Failed to open ticket');
    }
  };

  const act = async (path, body, msg) => {
    try {
      const r = await fetch(API(path), { method: 'POST', headers: headers(), body: JSON.stringify(body || {}) });
      if (r.ok) {
        if (msg) toast.success(msg);
        if (openId) openTicket(openId);
        loadList();
        loadStats();
      } else {
        toast.error('Action failed');
      }
    } catch {
      toast.error('Action failed');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await act(`/ticket/${openId}/reply`, { Message: reply });
    setReply('');
  };

  const markRead = async (n) => {
    try {
      await fetch(API(`/admin/notifications/${n.id}/read`), { method: 'POST', headers: headers() });
    } catch {}
    setNotifEl(null);
    loadNotif();
    openTicket(n.ticketId);
  };

  const exportExcel = async () => {
    try {
      const r = await fetch(API(`/admin/tickets?${qs({ page: 1, pageSize: 10000 })}`), { headers: headers() });
      if (!r.ok) return toast.error('Export failed');
      const d = await r.json();
      const data = (d.items || []).map((t) => ({
        TicketID: t.id,
        Company: t.companyName,
        Customer: t.raisedByName,
        Subject: t.subject,
        Type: t.complaintType,
        Priority: t.priority,
        Status: t.status,
        AssignedTo: t.assignedToName || '',
        Created: t.createdOn ? new Date(t.createdOn).toLocaleString('en-GB') : '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Complaints');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'complaints.xlsx');
    } catch {
      toast.error('Export failed');
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const t = detail?.ticket;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5' fontWeight={800}>🛡️ Support Management</Typography>
        <IconButton onClick={(e) => setNotifEl(e.currentTarget)}>
          <Badge badgeContent={notif.count} color='error'>
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Menu anchorEl={notifEl} open={!!notifEl} onClose={() => setNotifEl(null)}>
          {notif.items.length === 0 ? (
            <MenuItem disabled>No new notifications</MenuItem>
          ) : (
            notif.items.map((n) => (
              <MenuItem key={n.id} onClick={() => markRead(n)} sx={{ maxWidth: 320, whiteSpace: 'normal' }}>
                {n.message}
              </MenuItem>
            ))
          )}
        </Menu>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}><Stat label='New Complaints' value={stats.newComplaints} color='#F59E0B' /></Grid>
        <Grid item xs={6} md={3}><Stat label='Pending' value={stats.pending} color='#3B82F6' /></Grid>
        <Grid item xs={6} md={3}><Stat label='Resolved' value={stats.resolved} color='#16A34A' /></Grid>
        <Grid item xs={6} md={3}><Stat label='High Priority' value={stats.highPriority} color='#DC2626' /></Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
        <Grid container spacing={1.5} alignItems='center'>
          <Grid item xs={6} sm={2}><TextField fullWidth size='small' type='date' label='From' InputLabelProps={{ shrink: true }} value={filters.dateFrom} onChange={setF('dateFrom')} /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size='small' type='date' label='To' InputLabelProps={{ shrink: true }} value={filters.dateTo} onChange={setF('dateTo')} /></Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size='small' select label='Status' value={filters.status} onChange={setF('status')}>
              <MenuItem value='all'>All</MenuItem>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size='small' select label='Priority' value={filters.priority} onChange={setF('priority')}>
              <MenuItem value='all'>All</MenuItem>
              {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size='small' select label='Type' value={filters.type} onChange={setF('type')}>
              <MenuItem value='all'>All</MenuItem>
              {TYPES.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size='small' select label='Company' value={filters.companyId} onChange={setF('companyId')}>
              <MenuItem value=''>All</MenuItem>
              {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size='small' label='Customer / Subject' value={filters.customer} onChange={setF('customer')} /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size='small' label='Ticket ID' value={filters.ticketId} onChange={setF('ticketId')} /></Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size='small' select label='Sort' value={filters.sort} onChange={setF('sort')}>
              <MenuItem value='newest'>Newest</MenuItem>
              <MenuItem value='oldest'>Oldest</MenuItem>
              <MenuItem value='priority'>Priority</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5} sx={{ textAlign: 'right' }}>
            <Button variant='outlined' onClick={exportExcel}>Export Excel</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#F9FAFB' }}>
                {['#', 'Company', 'Customer', 'Subject', 'Type', 'Priority', 'Status', 'Created'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align='center' sx={{ py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align='center' sx={{ py: 5, color: '#6B7280' }}>No complaints found</TableCell></TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTicket(r.id)}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.companyName || '—'}</TableCell>
                    <TableCell>{r.raisedByName}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}><Typography noWrap>{r.subject}</Typography></TableCell>
                    <TableCell>{r.complaintType}</TableCell>
                    <TableCell><Chip size='small' label={r.priority} color={prioColor(r.priority)} variant='outlined' /></TableCell>
                    <TableCell><Chip size='small' label={r.status} color={statusColor(r.status)} /></TableCell>
                    <TableCell>{r.createdOn ? new Date(r.createdOn).toLocaleDateString('en-GB') : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant='caption' color='#6B7280'>{total} complaint(s)</Typography>
          <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size='small' color='primary' />
        </Box>
      </Paper>

      {/* Manage dialog */}
      <Dialog open={!!openId} onClose={() => setOpenId(null)} fullWidth maxWidth='sm'>
        <DialogTitle>Ticket #{openId}</DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <CircularProgress size={26} />
          ) : (
            <>
              <Typography fontWeight={700}>{t.subject}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {t.companyId ? '' : ''}{t.raisedByName} • {t.complaintType} • {new Date(t.createdOn).toLocaleString('en-GB')}
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>{t.description}</Typography>
              {t.attachment && (<div><a href={t.attachment} target='_blank' rel='noreferrer'>📎 View attachment</a></div>)}

              {/* Manage controls */}
              <Grid container spacing={1.5} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField fullWidth size='small' select label='Status' value={t.status}
                    onChange={(e) => act(`/admin/ticket/${openId}/status`, { Value: e.target.value }, 'Status updated')}>
                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size='small' select label='Priority' value={t.priority}
                    onChange={(e) => act(`/admin/ticket/${openId}/priority`, { Value: e.target.value }, 'Priority updated')}>
                    {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size='small' label='Assign to (name)' defaultValue={t.assignedToName || ''}
                    onBlur={(e) => act(`/admin/ticket/${openId}/assign`, { Name: e.target.value }, 'Assigned')} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={700} mb={1}>Conversation</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {detail.replies.length === 0 && <Typography variant='body2' color='text.secondary'>No replies yet.</Typography>}
                {detail.replies.map((r) => (
                  <Paper key={r.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, maxWidth: '85%',
                    alignSelf: r.byMaster ? 'flex-end' : 'flex-start',
                    bgcolor: r.byMaster ? '#EEF2FF' : '#ECFDF5' }}>
                    <Typography variant='caption' fontWeight={700}>{r.byMaster ? '🛡️ You' : r.byName}</Typography>
                    <Typography variant='body2'>{r.message}</Typography>
                  </Paper>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth size='small' placeholder='Reply to customer…' value={reply}
                  onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendReply()} />
                <Button variant='contained' onClick={sendReply}>Send</Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
